import { type FC, Suspense, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
	type MetaAdsAudienceListItem,
	MetaAdsAudienceStatus,
	MetaAdsAudienceType,
} from "shared";
import { Icon } from "../common/Icon";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { AppPage } from "../layout/AppLayout";
import { ModalCreateAudience } from "./audiences/ModalCreateAudience";
import { ModalCreateLookalike } from "./audiences/ModalCreateLookalike";
import { useAudienceActions, useAudiences } from "./useAudiences";
import { useCanEditMetaAds } from "./useMyMetaAdsRole";

const TYPE_LABELS: Record<MetaAdsAudienceType, string> = {
	[MetaAdsAudienceType.CUSTOM_LIST]: "Customer List",
	[MetaAdsAudienceType.CUSTOM_WEBSITE]: "Website",
	[MetaAdsAudienceType.CUSTOM_ENGAGEMENT]: "Engagement",
	[MetaAdsAudienceType.LOOKALIKE]: "Lookalike",
};

const STATUS_BADGE: Record<MetaAdsAudienceStatus, string> = {
	[MetaAdsAudienceStatus.POPULATING]: "bg-warning",
	[MetaAdsAudienceStatus.READY]: "bg-success",
	[MetaAdsAudienceStatus.TOO_SMALL]: "bg-secondary",
	[MetaAdsAudienceStatus.ERROR]: "bg-danger",
};

const AudiencesTable: FC<{
	onOpenCreate: () => void;
	onOpenLookalike: () => void;
}> = ({ onOpenCreate, onOpenLookalike }) => {
	const { data: audiences } = useAudiences();
	const { renameAudience, deleteAudience, refreshStatus } =
		useAudienceActions();
	const canEdit = useCanEditMetaAds();

	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<string>("all");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [renamingId, setRenamingId] = useState<string | null>(null);
	const [renameValue, setRenameValue] = useState("");

	const filtered = useMemo(() => {
		let result = audiences;
		if (search) {
			const q = search.toLowerCase();
			result = result.filter((a) => a.name.toLowerCase().includes(q));
		}
		if (typeFilter !== "all") {
			result = result.filter((a) => a.type === typeFilter);
		}
		if (statusFilter !== "all") {
			result = result.filter((a) => a.status === statusFilter);
		}
		return result;
	}, [audiences, search, typeFilter, statusFilter]);

	const handleRename = async (id: string) => {
		if (!renameValue.trim()) return;
		await renameAudience.mutateAsync({ id, name: renameValue });
		setRenamingId(null);
	};

	const handleDelete = (audience: MetaAdsAudienceListItem) => {
		if (!confirm(`Delete audience "${audience.name}"? This cannot be undone.`))
			return;
		deleteAudience.mutate(audience.id);
	};

	return (
		<>
			<div
				style={{
					display: "flex",
					gap: 12,
					alignItems: "center",
					flexWrap: "wrap",
					marginBottom: 16,
				}}
			>
				<input
					type="text"
					className="form-control form-control-sm"
					style={{ maxWidth: 250 }}
					placeholder="Search audiences..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<Select
					className="form-select form-select-sm"
					style={{ maxWidth: 160 }}
					value={typeFilter}
					onChange={(e) => setTypeFilter(e.target.value)}
					aria-label="Filter by type"
				>
					<option value="all">All Types</option>
					{Object.values(MetaAdsAudienceType).map((t) => (
						<option key={t} value={t}>
							{TYPE_LABELS[t]}
						</option>
					))}
				</Select>
				<Select
					className="form-select form-select-sm"
					style={{ maxWidth: 160 }}
					value={statusFilter}
					onChange={(e) => setStatusFilter(e.target.value)}
					aria-label="Filter by status"
				>
					<option value="all">All Statuses</option>
					{Object.values(MetaAdsAudienceStatus).map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</Select>
				{canEdit && (
					<div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
						<button
							type="button"
							className="btn btn-sm btn-primary"
							onClick={onOpenCreate}
						>
							<Icon name="add" size={16} /> Create Audience
						</button>
						<button
							type="button"
							className="btn btn-sm btn-outline-primary"
							onClick={onOpenLookalike}
						>
							<Icon name="group" size={16} /> Create Lookalike
						</button>
					</div>
				)}
			</div>

			<Card>
				<Card.Header>Audiences ({filtered.length})</Card.Header>
				<Card.Body style={{ padding: 0 }}>
					{filtered.length === 0 ? (
						<div style={{ padding: 24, textAlign: "center" }}>
							<p className="text-muted" style={{ marginBottom: 0 }}>
								{audiences.length === 0
									? "No audiences yet. Create your first audience to get started."
									: "No audiences match your filters."}
							</p>
						</div>
					) : (
						<div style={{ overflowX: "auto" }}>
							<table className="table" style={{ marginBottom: 0 }}>
								<thead>
									<tr>
										<th>Name</th>
										<th>Type</th>
										<th>Source</th>
										<th>Size</th>
										<th>Status</th>
										<th>Created</th>
										{canEdit && <th>Actions</th>}
									</tr>
								</thead>
								<tbody>
									{filtered.map((audience) => (
										<tr key={audience.id}>
											<td>
												{renamingId === audience.id ? (
													<div
														style={{
															display: "flex",
															gap: 4,
															alignItems: "center",
														}}
													>
														<input
															type="text"
															className="form-control form-control-sm"
															value={renameValue}
															onChange={(e) => setRenameValue(e.target.value)}
															onKeyDown={(e) => {
																if (e.key === "Enter")
																	handleRename(audience.id);
																if (e.key === "Escape") setRenamingId(null);
															}}
															// biome-ignore lint/a11y/noAutofocus: rename input should auto-focus
															autoFocus
														/>
														<button
															type="button"
															className="btn btn-sm btn-primary"
															onClick={() => handleRename(audience.id)}
														>
															Save
														</button>
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															onClick={() => setRenamingId(null)}
														>
															Cancel
														</button>
													</div>
												) : (
													<strong>{audience.name}</strong>
												)}
											</td>
											<td>{TYPE_LABELS[audience.type] ?? audience.type}</td>
											<td>{audience.sourceDescription}</td>
											<td>
												{audience.approximateSize > 0
													? audience.approximateSize.toLocaleString()
													: "-"}
											</td>
											<td>
												<span
													className={`badge ${STATUS_BADGE[audience.status] ?? "bg-secondary"}`}
												>
													{audience.status}
												</span>
											</td>
											<td>
												{new Date(audience.createdAt).toLocaleDateString()}
											</td>
											{canEdit && (
												<td>
													<div
														style={{
															display: "flex",
															gap: 4,
														}}
													>
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															title="Rename"
															onClick={() => {
																setRenamingId(audience.id);
																setRenameValue(audience.name);
															}}
														>
															<Icon name="edit" size={14} />
														</button>
														<button
															type="button"
															className="btn btn-sm btn-outline-secondary"
															title="Refresh status"
															onClick={() => refreshStatus.mutate(audience.id)}
														>
															<Icon name="sync" size={14} />
														</button>
														<button
															type="button"
															className="btn btn-sm btn-outline-danger"
															title="Delete"
															onClick={() => handleDelete(audience)}
														>
															<Icon name="delete" size={14} />
														</button>
													</div>
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</Card.Body>
			</Card>
		</>
	);
};

export const PageMetaAdsAudiences: FC = () => {
	const [showCreate, setShowCreate] = useState(false);
	const [showLookalike, setShowLookalike] = useState(false);

	return (
		<AppPage title="Audiences">
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load audiences.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading audiences...</p>}>
						<AudiencesTableWrapper
							showCreate={showCreate}
							showLookalike={showLookalike}
							onShowCreate={setShowCreate}
							onShowLookalike={setShowLookalike}
						/>
					</Suspense>
				</ErrorBoundary>
			</div>
		</AppPage>
	);
};

const AudiencesTableWrapper: FC<{
	showCreate: boolean;
	showLookalike: boolean;
	onShowCreate: (show: boolean) => void;
	onShowLookalike: (show: boolean) => void;
}> = ({ showCreate, showLookalike, onShowCreate, onShowLookalike }) => {
	const { data: audiences } = useAudiences();

	return (
		<>
			<AudiencesTable
				onOpenCreate={() => onShowCreate(true)}
				onOpenLookalike={() => onShowLookalike(true)}
			/>
			{showCreate && (
				<ModalCreateAudience onClose={() => onShowCreate(false)} />
			)}
			{showLookalike && (
				<ModalCreateLookalike
					audiences={audiences}
					onClose={() => onShowLookalike(false)}
				/>
			)}
		</>
	);
};
